import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalSidebar } from '../ui/sidebar';
import { X, Search, Check, MessageSquare, SquarePen, Upload } from 'lucide-react';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { useStreamChat } from '../../context/StreamChatContext';
import { getUserSettings, getAllUsers } from '../../utils/api';
import { Chat } from 'stream-chat-react';
import styles from './Chat.module.css';

import CustomChannelList from './ChannelList/CustomChannelList';
import CustomChannelHeader from './Header/CustomChannelHeader';
import CustomMessageList from './Messages/CustomMessageList';
import CustomMessageInput from './Input/CustomMessageInput';
import { createStreamChatShareModal } from './utils/filterShareUtils';
import { createAIChatShareModal } from './utils/aiChatShareUtils';
import ChatAvatar from './parts/ChatAvatar';
import SupportPrompt from './parts/SupportPrompt';
import { ChatMediaProvider } from './parts/ChatMediaContext';
import { classifySupportQuery } from './utils/supportIntent';
import { channelDisplayName } from './utils/chatFormat';
import { isLodgeicStaffEmail } from '../Admin/users/userModel';

const StreamChatBox = ({ isOpen, onClose, userId, isSidebarCollapsed = true }) => {
  // Clear the desktop sidebar: inset shell (16rem open / 3rem icon rail +
  // gutters) via context; classic shell via the prop its layout passes.
  const sidebar = useOptionalSidebar();
  const sidebarOffset = sidebar
    ? (sidebar.state === 'collapsed' ? '5rem' : '17rem')
    : (isSidebarCollapsed ? '6rem' : '17rem');
  const { chatClient, loading, error, initializeChat, user, fetchUnreadCount } = useStreamChat();
  const [isAnimating, setIsAnimating] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [isMobileChannelView, setIsMobileChannelView] = useState(false);

  // New channel creation state
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Lodgeic staff (@lodgeic.io) aren't reachable in Team Chat for customers:
  // they're left out of the people picker, and searching for them (or for
  // "lodgeic", "support"…) offers support chat instead.
  const [orgUsers, setOrgUsers] = useState([]);
  const viewerIsStaff = isLodgeicStaffEmail(userEmail);
  const supportEnabled = !viewerIsStaff;
  const staffNames = orgUsers.filter((u) => u.isStaff).map((u) => u.name);

  // Messages being sent from this browser (Sending… / Not sent rows).
  const [pending, setPending] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const composerRef = useRef(null);

  const isModal = onClose !== undefined;
  const isMobile = window.innerWidth <= 768;

  // Control animation timing for modal mode
  useEffect(() => {
    if (!isModal) return;

    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        document.body.style.overflow = '';
      }, 300);
    }
  }, [isOpen, isModal]);

  // Fetch user email immediately when component mounts or opens
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const userEmailFromCognito = attributes?.email;

        if (!userEmailFromCognito) {
          console.error('[StreamChatBox] No email found in user attributes');
          return;
        }

        setUserEmail(userEmailFromCognito);
      } catch (error) {
        console.error('[StreamChatBox] Error fetching user email:', error);
      }
    };

    if (isOpen && !userEmail) {
      fetchUserEmail();
    }
  }, [isOpen, userEmail]);

  // Separate effect for initializing chat when we have user email
  useEffect(() => {
    const initializeChatWithUserData = async () => {
      try {
        const userSettings = await getUserSettings(userEmail);

        if (userSettings) {
          setOrganizationId(userSettings.organizationId);
          await initializeChat(userEmail, userSettings);
        }
      } catch (error) {
        console.error('[StreamChatBox] Error initializing chat:', error);
      }
    };

    if (isOpen && userEmail && !chatClient && !loading) {
      initializeChatWithUserData();
    }
  }, [isOpen, userEmail, chatClient, loading, initializeChat]);

  const handleChannelSelect = async (channel) => {
    setCurrentChannel(channel);
    if (isMobile) {
      setIsMobileChannelView(true);
    }

    try {
      await channel.markRead();
      setTimeout(() => {
        if (fetchUnreadCount) {
          fetchUnreadCount();
        }
      }, 100);
    } catch (error) {
      console.error('[StreamChatBox] Error marking channel as read:', error);
    }
  };

  const handleBack = () => {
    setIsMobileChannelView(false);
    setCurrentChannel(null);
  };

  const handleChannelDeleted = () => {
    setCurrentChannel(null);
    if (isMobile) {
      setIsMobileChannelView(false);
    }
  };

  // Fetch ALL users from the organization (not just active chat users)
  const fetchUsersFromOrganization = async () => {
    try {
      const currentOrgCode = localStorage.getItem('currentOrgCode');

      if (!currentOrgCode) {
        console.error('No current organization code found in localStorage');
        return [];
      }

      const allUsers = await getAllUsers();

      const orgUsers = allUsers.filter(u => {
        const hasOrgAccess = u.orgAccess && u.orgAccess[currentOrgCode];
        const isNotCurrentUser = u.email !== userEmail;
        const isNotDeleted = !u.isDeleted;

        return hasOrgAccess && isNotCurrentUser && isNotDeleted;
      });

      return orgUsers.map(u => ({
        id: u.email.replace(/[^a-zA-Z0-9@_-]/g, '_').toLowerCase(),
        email: u.email,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        image: u.profilePictureThumb || u.profilePicture,
        isStaff: isLodgeicStaffEmail(u.email),
      }));
    } catch (error) {
      console.error('Error fetching organization users:', error);
      return [];
    }
  };

  // Who this viewer may start a conversation with (customers: everyone but Lodgeic staff).
  const reachableUsers = useCallback(
    (users) => (viewerIsStaff ? users : users.filter((u) => !u.isStaff)),
    [viewerIsStaff],
  );

  // Staff names feed the support matcher in both searches.
  useEffect(() => {
    if (!chatClient || !userEmail) return;
    let cancelled = false;
    fetchUsersFromOrganization().then((users) => { if (!cancelled) setOrgUsers(users); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, userEmail]);

  const closeNewMessage = () => {
    setShowCreateChannelModal(false);
    setSelectedUsers([]);
    setUserSearchQuery('');
  };

  // Optimistic send: the row shows Sending… at once and Not sent (Retry /
  // Discard) on failure. The client-chosen id lets the list swap the pending
  // row for Stream's copy of the same message.
  const sendChatMessage = useCallback(async (channel, payload, retryId) => {
    if (!channel) return;
    const id = retryId || (window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
    const entry = {
      id,
      cid: channel.cid,
      channel,
      payload,
      text: payload.text,
      attachments: payload.attachments,
      user: chatClient?.user,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    setPending((prev) => [...prev.filter((p) => p.id !== id), entry]);
    const markSent = () => {
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'sent' } : p)));
      setTimeout(() => setPending((prev) => prev.filter((p) => p.id !== id)), 15000);
    };
    try {
      await channel.sendMessage({
        id,
        text: payload.text,
        attachments: payload.attachments?.length ? payload.attachments : undefined,
      });
      markSent();
    } catch (error) {
      // A retry after a timed-out first attempt that actually landed.
      if (retryId && /already exists/i.test(error?.message || '')) { markSent(); return; }
      console.error('[StreamChatBox] Error sending message:', error);
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'failed' } : p)));
    }
  }, [chatClient]);

  // An in-app link opened from the floating (modal) chat closes it.
  useEffect(() => {
    if (!isModal) return undefined;
    const onNavigated = () => onClose?.();
    window.addEventListener('lodgeic:chat:navigated', onNavigated);
    return () => window.removeEventListener('lodgeic:chat:navigated', onNavigated);
  }, [isModal, onClose]);

  const handleCreateChannel = async () => {
    const currentOrgCode = localStorage.getItem('currentOrgCode');

    if (!userEmail || !currentOrgCode) {
      console.error('[StreamChatBox] User data not loaded yet.', { userEmail, currentOrgCode });
      if (!userEmail) {
        try {
          const attributes = await fetchUserAttributes();
          const email = attributes?.email;
          if (email) {
            setUserEmail(email);
            setTimeout(() => handleCreateChannel(), 100);
            return;
          }
        } catch (error) {
          console.error('[StreamChatBox] Failed to fetch user email:', error);
        }
      }
      return;
    }

    setShowCreateChannelModal(true);
    setUserSearchQuery('');

    try {
      setLoadingUsers(true);
      const users = await fetchUsersFromOrganization();
      setOrgUsers(users);
      setAvailableUsers(reachableUsers(users));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.find(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const createNewChannel = async () => {
    if (!chatClient || selectedUsers.length === 0) return;

    setCreatingChannel(true);
    try {
      const memberIds = [user.id, ...selectedUsers.map(u => u.id)];

      const channel = chatClient.channel('messaging', {
        members: memberIds,
        name: selectedUsers.length === 1
          ? `Chat with ${selectedUsers[0].name}`
          : `Group chat with ${selectedUsers.map(u => u.name).join(', ')}`,
      });

      await channel.create();
      await channel.watch();

      closeNewMessage();
      handleChannelSelect(channel);

      if (isMobile) {
        setIsMobileChannelView(true);
      }
    } catch (error) {
      console.error('Error creating channel:', error);
    } finally {
      setCreatingChannel(false);
    }
  };

  // Filter users by search query
  const filteredUsers = userSearchQuery
    ? availableUsers.filter(u =>
        u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
      )
    : availableUsers;

  // Send filter share message function
  const sendFilterShareMessage = async (filterData) => {
    if (!chatClient) {
      console.error('No chat client available for sending filter share message');
      return;
    }

    try {
      const filter = { members: { $in: [user.id] } };
      const sort = { last_message_at: -1 };
      const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });

      const orgUsers = reachableUsers(await fetchUsersFromOrganization());

      await createStreamChatShareModal(filterData, channels, user, chatClient, orgUsers);
    } catch (error) {
      console.error('Error in sendFilterShareMessage:', error);
      throw error;
    }
  };

  // Make sendFilterShareMessage available globally for PowerBI integration
  useEffect(() => {
    if (chatClient && user) {
      window.sendFilterShareMessage = sendFilterShareMessage;
    }
    return () => {
      window.sendFilterShareMessage = null;
    };
  }, [chatClient, user]);

  // Native report view share: the URL carries the complete report state
  // (filters, drill-down expansion, scroll position), so the attachment is
  // just the link + display labels. Reuses the same channel-selection modal.
  const sendNativeReportShareMessage = async ({ url, reportCategory, reportTitle, subtitle }) => {
    if (!chatClient) {
      console.error('No chat client available for sending native report share message');
      throw new Error('Chat unavailable');
    }
    const filter = { members: { $in: [user.id] } };
    const sort = { last_message_at: -1 };
    const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });
    const orgUsers = reachableUsers(await fetchUsersFromOrganization());
    await createStreamChatShareModal(
      {
        attachmentType: 'native_report_share',
        url,
        reportCategory,
        reportTitle,
        subtitle,
        reportName: reportCategory ? `${reportCategory} · ${reportTitle}` : reportTitle,
        timestamp: new Date().toISOString(),
      },
      channels,
      user,
      chatClient,
      orgUsers
    );
  };

  useEffect(() => {
    if (chatClient && user) {
      window.sendNativeReportShareMessage = sendNativeReportShareMessage;
    }
    return () => {
      window.sendNativeReportShareMessage = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, user]);

  // Send AI chat session share message. Mirrors sendFilterShareMessage but
  // opens the AI-specific share modal, which forks the session server-side
  // to each recipient before posting a Stream Chat notification.
  const sendAIChatShareMessage = async (sessionInfo) => {
    if (!chatClient) {
      console.error('No chat client available for sending AI chat share message');
      return;
    }
    try {
      const filter = { members: { $in: [user.id] } };
      const sort = { last_message_at: -1 };
      const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });
      const orgUsers = reachableUsers(await fetchUsersFromOrganization());
      await createAIChatShareModal(sessionInfo, channels, user, chatClient, orgUsers);
    } catch (error) {
      console.error('Error in sendAIChatShareMessage:', error);
      throw error;
    }
  };

  // Make sendAIChatShareMessage available globally so AIChat.js can open the
  // share modal without importing the StreamChat context directly.
  useEffect(() => {
    if (chatClient && user) {
      window.sendAIChatShareMessage = sendAIChatShareMessage;
    }
    return () => {
      window.sendAIChatShareMessage = null;
    };
  }, [chatClient, user]);

  // Listen for channel selection events from filter sharing
  useEffect(() => {
    const handleSelectChatChannel = (event) => {
      const { channel } = event.detail;
      handleChannelSelect(channel);
    };

    const checkForStoredSelectedChannel = () => {
      try {
        const storedChannel = sessionStorage.getItem('selectedChatChannel');
        if (storedChannel && chatClient) {
          const { id, timestamp } = JSON.parse(storedChannel);

          if (Date.now() - timestamp < 30000) {
            sessionStorage.removeItem('selectedChatChannel');

            setTimeout(async () => {
              try {
                const channel = chatClient.channel('messaging', id);
                await channel.watch();
                handleChannelSelect(channel);
              } catch (error) {
                console.error('[StreamChatBox] Error selecting stored channel:', error);
              }
            }, 500);
          } else {
            sessionStorage.removeItem('selectedChatChannel');
          }
        }
      } catch (error) {
        console.error('[StreamChatBox] Error checking for stored selected channel:', error);
      }
    };

    window.addEventListener('selectChatChannel', handleSelectChatChannel);

    if (chatClient && isOpen) {
      checkForStoredSelectedChannel();
    }

    return () => {
      window.removeEventListener('selectChatChannel', handleSelectChatChannel);
    };
  }, [chatClient, isOpen]);

  if (isModal && !isOpen && !isAnimating) return null;

  // Loading state
  if (loading) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 320, padding: 'var(--sp-8)', alignItems: 'center', gap: 12 }}>
          <div className={styles.spinner} />
          <p className={styles.emptyText}>Connecting to chat…</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 400, padding: 'var(--sp-6)', gap: 8 }}>
          <h3 className={styles.emptyTitle}>Couldn’t connect to chat</h3>
          <p className={styles.emptyText} style={{ maxWidth: 'none' }}>{error.message}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => initializeChat(userEmail, { email: userEmail })} className={styles.btnInk}>
              Try again
            </button>
            {isModal && (
              <button type="button" onClick={onClose} className={styles.btnGhost}>Close</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!chatClient || !user) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 320, padding: 'var(--sp-8)', alignItems: 'center', gap: 12 }}>
          <p className={styles.emptyText}>Chat isn’t connected.</p>
          {isModal && (
            <button type="button" onClick={onClose} className={styles.btnGhost}>Close</button>
          )}
        </div>
      </div>
    );
  }

  const conversationName = currentChannel ? channelDisplayName(currentChannel, chatClient.user?.id) : '';
  const query = userSearchQuery.trim().toLowerCase();
  const supportMatch = supportEnabled && query ? classifySupportQuery(userSearchQuery, { staffNames }) : null;

  const newMessageDialog = showCreateChannelModal && (
    <div
      className={styles.modalBackdrop}
      style={{ zIndex: 'calc(var(--z-modal) + 10)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) closeNewMessage(); }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-new-message-title"
        onKeyDown={(e) => { if (e.key === 'Escape') closeNewMessage(); }}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderText}>
            <h2 id="chat-new-message-title" className={styles.modalTitle}>New message</h2>
            <p className={styles.modalSubtitle}>Message people at your properties</p>
          </div>
          <button type="button" onClick={closeNewMessage} className={styles.modalClose} aria-label="Close">
            <X size={17} />
          </button>
        </div>

        <div className={styles.modalSearch}>
          <label className={styles.searchWrap}>
            <span className={styles.srOnly}>To</span>
            <Search size={15} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by name or email"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className={styles.searchInput}
              autoFocus
            />
          </label>
        </div>

        {selectedUsers.length > 0 && (
          <div className={styles.selectedChips}>
            {selectedUsers.map((u) => (
              <button key={u.id} type="button" className={styles.selectedChip} onClick={() => toggleUserSelection(u)} aria-label={`Remove ${u.name}`}>
                {u.name}
                <X size={12} aria-hidden="true" />
              </button>
            ))}
          </div>
        )}

        <div className={styles.modalBody}>
          {supportMatch?.strength === 'strong' && (
            <SupportPrompt match={supportMatch} layout="wide" onOpened={closeNewMessage} />
          )}

          {loadingUsers ? (
            <div className={styles.emptyState} style={{ padding: '32px 0' }}>
              <div className={styles.spinner} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className={styles.listSection} style={{ padding: 0 }}>
                {query ? 'Your team' : `${filteredUsers.length} ${filteredUsers.length === 1 ? 'person' : 'people'}`}
              </span>
              {filteredUsers.length === 0 ? (
                <span className={styles.listNote} style={{ padding: 0 }}>
                  {query
                    ? `No one at your properties matches “${userSearchQuery.trim()}”.`
                    : 'No one else in your organization has Team Chat yet.'}
                </span>
              ) : (
                <div className={styles.userList}>
                  {filteredUsers.map((u) => {
                    const isSelected = selectedUsers.some((s) => s.id === u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`${styles.userItem} ${isSelected ? styles.selected : ''}`}
                        onClick={() => toggleUserSelection(u)}
                        aria-pressed={isSelected}
                      >
                        <ChatAvatar name={u.name} id={u.id} image={u.image} />
                        <span className={styles.userItemText}>
                          <span className={styles.userItemName}>{u.name}</span>
                          <span className={styles.userItemEmail}>{u.email}</span>
                        </span>
                        <span className={styles.userCheck} aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {supportMatch?.strength === 'soft' && (
            <SupportPrompt match={supportMatch} onOpened={closeNewMessage} />
          )}
        </div>

        <div className={styles.modalFooter}>
          <span className={styles.modalFooterNote}>
            {selectedUsers.length > 1 ? `Group of ${selectedUsers.length + 1}` : selectedUsers.length === 1 ? 'Direct message' : ''}
          </span>
          <div className={styles.modalFooterActions}>
            <button type="button" onClick={closeNewMessage} className={styles.btnGhost}>Cancel</button>
            <button
              type="button"
              onClick={createNewChannel}
              disabled={selectedUsers.length === 0 || creatingChannel}
              className={styles.btnPrimary}
            >
              {creatingChannel ? 'Starting…' : 'Start chat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const chatContent = (
    <Chat client={chatClient}>
      <ChatMediaProvider>
        <div className={styles.chatRoot}>
          {/* Channel List */}
          <div style={{ display: isMobile && isMobileChannelView ? 'none' : 'flex', width: isMobile ? '100%' : undefined, flexShrink: 0 }}>
            <CustomChannelList
              organizationId={organizationId}
              onChannelSelect={handleChannelSelect}
              onCreateChannel={handleCreateChannel}
              activeChannelId={currentChannel?.id}
              isMobile={isMobile}
              supportEnabled={supportEnabled}
              staffNames={staffNames}
            />
          </div>

          {/* Chat Area */}
          {currentChannel && (
            <div
              className={styles.chatArea}
              style={{ display: isMobile && !isMobileChannelView ? 'none' : undefined }}
              onDragEnter={(e) => { if (hasFiles(e)) { dragDepth.current += 1; setDragActive(true); } }}
              onDragOver={(e) => { if (hasFiles(e)) e.preventDefault(); }}
              onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragActive(false); }}
              onDrop={(e) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                dragDepth.current = 0;
                setDragActive(false);
                composerRef.current?.addFiles(e.dataTransfer.files);
              }}
            >
              <CustomChannelHeader
                onBack={handleBack}
                isMobile={isMobile}
                channel={currentChannel}
                onDeleteChannel={handleChannelDeleted}
              />
              {/* Keys must differ between siblings: a shared key leaves the
                  previous conversation's list mounted beside the new one. */}
              <CustomMessageList
                key={`list:${currentChannel.cid}`}
                channel={currentChannel}
                conversationName={conversationName}
                pending={pending}
                onRetryPending={(entry) => entry && sendChatMessage(entry.channel, entry.payload, entry.id)}
                onDiscardPending={(entry) => entry && setPending((prev) => prev.filter((p) => p.id !== entry.id))}
              />
              <CustomMessageInput
                key={`composer:${currentChannel.cid}`}
                ref={composerRef}
                channel={currentChannel}
                conversationName={conversationName}
                onSend={(payload) => sendChatMessage(currentChannel, payload)}
              />
              {dragActive && (
                <div className={styles.dropOverlay} aria-hidden="true">
                  <span className={styles.supportIcon} style={{ width: 40, height: 40 }}><Upload size={19} /></span>
                  <span className={styles.dropTitle}>Drop to share with {conversationName}</span>
                  <span className={styles.dropSub}>Images, PDFs and spreadsheets · up to 100 MB each</span>
                </div>
              )}
            </div>
          )}

          {/* Empty state when no channel selected on desktop */}
          {!currentChannel && !isMobile && (
            <div className={styles.emptyPane}>
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}><MessageSquare size={20} /></div>
                <h3 className={styles.emptyTitle}>Team Chat</h3>
                <p className={styles.emptyText}>
                  Pick a conversation, or start one with the people you work with at your properties.
                </p>
                <button type="button" onClick={handleCreateChannel} className={`${styles.btnInk} ${styles.btnSm}`} style={{ marginTop: 14 }}>
                  <SquarePen size={14} aria-hidden="true" /> New message
                </button>
              </div>
            </div>
          )}
        </div>
      </ChatMediaProvider>
    </Chat>
  );

  // Modal mode
  if (isModal) {
    return (
      <div className={styles.modalShell} style={{ opacity: isOpen ? 1 : 0 }}>
        <div className={styles.modalShellBackdrop} onClick={onClose} />
        <div
          className={styles.modalShellCard}
          style={{
            transform: isOpen ? 'scale(1)' : 'scale(0.97)',
            left: isMobile ? '1rem' : sidebarOffset,
          }}
        >
          <div className={styles.modalShellHeader}>
            <h2 className={styles.modalShellTitle}>Team Chat</h2>
            <button type="button" onClick={onClose} className={styles.modalClose} aria-label="Close chat">
              <X size={18} />
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {chatContent}
          </div>
        </div>
        {newMessageDialog}
      </div>
    );
  }

  // Embedded mode
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {chatContent}
      {newMessageDialog}
    </div>
  );
};

export default StreamChatBox;
